<template>
    <div class="public-base">
        <div class="mobile-menu-header">
            <button class="burger-button" @click="toggleMobileMenu">
                <svg class="burger-icon" :class="{ 'burger-icon--open': mobileMenuOpen }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path v-if="!mobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
                    <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
                <span>Меню</span>
            </button>
        </div>

        <div class="mobile-menu-container" :class="{ 'mobile-menu-container--open': mobileMenuOpen }">
            <menu-section
                :items="sections"
                :hash="hash"
                item-route-name="base-detail-item"
                @loadItems="handleLoadItems"
                @itemClick="handleItemClick"
            />
        </div>

        <v-row align="start">
            <v-col cols="2" class="d-none d-md-block desktop-menu">
                <div class="menu-wrapper">
                    <menu-section
                        :items="sections"
                        :hash="hash"
                        item-route-name="base-detail-item"
                        @loadItems="handleLoadItems"
                        @itemClick="handleItemClick"
                    />
                </div>
            </v-col>
            <v-col cols="12" md="10">
                <div class="content-wrapper-inner">
                    <div v-if="loading" class="loading-state">
                        <div class="loading-spinner">
                            <svg class="spinner" viewBox="0 0 50 50">
                                <circle class="spinner-circle" cx="25" cy="25" r="20" fill="none" stroke-width="4"></circle>
                            </svg>
                            <span class="loading-text">Загрузка...</span>
                        </div>
                    </div>

                    <div v-else-if="error" class="public-base__empty">
                        База недоступна.
                    </div>

                    <div v-else-if="selectedItem" class="section-item-container">
                        <div class="section-item-content">
                            <div
                                class="main-content-wrapper"
                                :class="{ 'main-content-wrapper--full': !selectedItemHasToc }"
                            >
                                <div class="content-body">
                                    <v-btn
                                        v-if="format !== 'selected_article'"
                                        class="public-base__back"
                                        small
                                        text
                                        @click="showHomepage"
                                    >
                                        Назад к обзору
                                    </v-btn>
                                    <div class="item-meta">
                                        <span class="created_at">Дата создания: {{ selectedItem.created_at }}</span>
                                    </div>
                                    <div class="item-title">
                                        <h1>{{ selectedItem.name }}</h1>
                                    </div>
                                    <public-tag-list :tags="selectedItem.tags" @select="searchByTag"></public-tag-list>
                                    <div v-if="selectedItem.description" class="description" v-html="selectedItem.description"></div>
                                    <div class="item-body froala-content" v-html="selectedItem.text"></div>
                                </div>
                                <aside v-if="selectedItemHasToc" class="toc-sidebar">
                                    <section-toc :content="selectedItem.text"></section-toc>
                                </aside>
                            </div>
                        </div>
                    </div>

                    <div v-else>
                        <div class="public-base__search">
                            <v-text-field
                                v-model="searchQuery"
                                class="public-base__search-input"
                                dense
                                outlined
                                hide-details
                                clearable
                                placeholder="Поиск по публичным статьям"
                                append-icon="mdi-magnify"
                                @keydown.enter.prevent="runSearch"
                                @click:clear="clearSearch"
                            />
                            <v-btn
                                class="public-base__search-button"
                                :loading="searchLoading"
                                :disabled="searchLoading"
                                @click="runSearch"
                            >
                                Найти
                            </v-btn>
                        </div>
                        <h1 class="public-base__title">{{ listTitle }}</h1>
                        <v-chip
                            v-if="searchActive && searchType === 'tag'"
                            class="public-base__tag-chip"
                            close
                            small
                            @click:close="clearSearch"
                        >Тег: {{ activeQuery }}</v-chip>
                        <div v-if="items.length === 0" class="public-base__empty">
                            Статьи не найдены.
                        </div>
                        <template v-else>
                            <v-card v-for="item in items" :key="item.id" class="card">
                                <v-card-title>
                                    <a href="#" @click.prevent="openItem(item.id)">{{ item.name }}</a>
                                </v-card-title>
                                <v-card-text v-html="item.description">
                                    {{ item.description }}
                                </v-card-text>
                                <v-card-text v-if="item.tags && item.tags.length" class="pt-0">
                                    <public-tag-list :tags="item.tags" @select="searchByTag"></public-tag-list>
                                </v-card-text>
                            </v-card>
                        </template>
                    </div>
                </div>
            </v-col>
        </v-row>

        <div class="menu-overlay" :class="{ 'menu-overlay--active': mobileMenuOpen }" @click="closeMobileMenu"></div>
    </div>
</template>

<script>
import Vue from 'vue';
import axios from 'axios';
import MenuSection from './MenuSection.vue';
import SectionToc from './SectionToc.vue';
import PublicTagList from './PublicTagList.vue';

export default {
    name: 'PublicBase',
    components: {
        MenuSection,
        SectionToc,
        PublicTagList,
    },
    props: {
        hash: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            loading: true,
            itemLoading: false,
            error: false,
            title: '',
            format: 'latest_articles',
            items: [],
            sections: [],
            loadedSections: {},
            selectedItem: null,
            mobileMenuOpen: false,
            searchQuery: '',
            searchLoading: false,
            searchActive: false,
            searchType: 'text',
            activeQuery: '',
        };
    },
    computed: {
        listTitle() {
            if (this.searchActive) {
                return this.searchType === 'tag'
                    ? `Статьи с тегом «${this.activeQuery}»`
                    : `Результаты поиска: ${this.activeQuery}`;
            }

            if (this.format === 'featured_articles') {
                return 'Избранные статьи';
            }

            return 'Последние статьи';
        },
        selectedItemHasToc() {
            return this.hasToc(this.selectedItem?.text || '');
        },
    },
    methods: {
        hasToc(content) {
            if (!content) {
                return false;
            }

            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = content;

            return Boolean(tempDiv.querySelector('nav.toc'));
        },
        toggleMobileMenu() {
            this.mobileMenuOpen = !this.mobileMenuOpen;
        },
        closeMobileMenu() {
            this.mobileMenuOpen = false;
        },
        handleLoadItems(data) {
            const { sectionId, hash, component } = data;

            if (this.loadedSections[sectionId]) {
                component.setItems(this.loadedSections[sectionId]);
                return;
            }

            axios.post('/rest/share/base/items', {
                sectionId,
                hash,
            }).then((response) => {
                const items = response.data.items || [];
                Vue.set(this.loadedSections, sectionId, items);
                component.setItems(items);
            }).catch(() => {
                Vue.set(this.loadedSections, sectionId, []);
                component.setItems([]);
            });
        },
        handleItemClick(item) {
            this.closeMobileMenu();
        },
        loadBase() {
            this.loading = true;
            this.error = false;
            axios.post(`/rest/share/base/${this.hash}`).then((response) => {
                this.title = response.data.title || '';
                this.format = response.data.format || 'latest_articles';
                this.items = response.data.items || [];
                this.sections = response.data.sections || [];
                this.selectedItem = response.data.item || null;
                if (this.$route.name === 'base-detail-item' && this.$route.params.itemId) {
                    this.loadItem(this.$route.params.itemId);
                }
            }).catch(() => {
                this.error = true;
            }).finally(() => {
                this.loading = false;
            });
        },
        openItem(itemId) {
            this.$router.push({
                name: 'base-detail-item',
                params: { itemId },
                query: { hash: this.hash },
            });
        },
        loadItem(itemId) {
            this.itemLoading = true;
            this.error = false;
            axios.post('/rest/share/base/item', {
                hash: this.hash,
                itemId,
            }).then((response) => {
                this.selectedItem = response.data.item;
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }).catch(() => {
                this.error = true;
            }).finally(() => {
                this.itemLoading = false;
            });
        },
        showHomepage() {
            this.selectedItem = null;
            if (this.$route.name === 'base-detail-item') {
                this.$router.push({ name: 'base-home', query: { hash: this.hash } });
            }
        },
        runSearch() {
            const query = this.searchQuery.trim();

            if (query === '') {
                this.clearSearch();
                return;
            }

            this.searchQuery = query;
            this.performSearch(query, 'text');
        },
        searchByTag(tag) {
            this.searchQuery = '';
            this.performSearch(tag, 'tag');
        },
        performSearch(query, type) {
            this.searchLoading = true;
            this.error = false;

            axios.post('/rest/share/base/search', {
                hash: this.hash,
                query,
                type,
            }).then((response) => {
                this.items = response.data.items || [];
                this.selectedItem = null;
                this.activeQuery = query;
                this.searchType = type;
                this.searchActive = true;
                this.closeMobileMenu();

                if (this.$route.name === 'base-detail-item') {
                    this.$router.push({ name: 'base-home', query: { hash: this.hash } });
                }
            }).catch(() => {
                this.error = true;
            }).finally(() => {
                this.searchLoading = false;
            });
        },
        clearSearch() {
            if (!this.searchActive && this.searchQuery === '') {
                return;
            }

            this.searchQuery = '';
            this.activeQuery = '';
            this.searchType = 'text';
            this.searchActive = false;
            this.showHomepage();
            this.loadBase();
        },
    },
    watch: {
        '$route.params.itemId'(itemId) {
            if (this.$route.name === 'base-detail-item' && itemId) {
                this.loadItem(itemId);
            }
        },
    },
    created() {
        this.loadBase();
    },
};
</script>

<style scoped>
.public-base {
    width: 100%;
}

.public-base__empty {
    align-items: center;
    display: flex;
    justify-content: center;
    min-height: 400px;
    padding: 40px;
}

.mobile-menu-header {
    display: none;
    padding: 10px 16px;
    background: #ffffff;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 16px;
    position: sticky;
    top: 0;
    z-index: 100;
}

.burger-button {
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    color: #374151;
    font-size: 16px;
    font-weight: 500;
    width: 100%;
    justify-content: flex-start;
}

.burger-button:hover {
    background-color: #f3f4f6;
    border-radius: 8px;
}

.burger-icon {
    width: 24px;
    height: 24px;
    transition: transform 0.3s;
}

.mobile-menu-container {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: 300px;
    max-width: 85vw;
    z-index: 1000;
    background: #ffffff;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease-in-out;
    transform: translateX(-100%);
    overflow: hidden;
}

.mobile-menu-container--open {
    transform: translateX(0);
}

.menu-overlay {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
    opacity: 0;
    transition: opacity 0.3s;
    pointer-events: none;
}

.menu-overlay--active {
    opacity: 1;
    pointer-events: auto;
}

.desktop-menu {
    padding: 0 !important;
}

.menu-wrapper {
    margin-top: 60px;
}

.content-wrapper-inner {
    width: 100%;
    padding: 0 16px;
    box-sizing: border-box;
    margin-top: 30px;
}

.v-col.cols-2,
.v-col.cols-12 {
    align-self: flex-start !important;
}

.v-row {
    align-items: flex-start !important;
}

:deep(.v-row) {
    align-items: flex-start !important;
}

:deep(.v-col) {
    align-self: flex-start !important;
}

.public-base__title {
    color: #1f2937;
    font-size: 28px;
    font-weight: 600;
    line-height: 1.3;
    margin: 0 0 25px;
}

.public-base__search {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
}

.public-base__search-input {
    flex: 1 1 auto;
}

.public-base__search-button {
    background-color: #1976d2 !important;
    color: #ffffff !important;
}

.public-base__tag-chip {
    margin-bottom: 16px;
}

.card {
    margin-bottom: 30px;
}

.card :deep(.v-card__text) {
    overflow-wrap: anywhere;
    white-space: normal;
}

.section-item-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 20px;
}

.main-content-wrapper {
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: 40px;
    padding: 30px;
    max-width: 1200px;
    margin: 0 auto;
}

.main-content-wrapper--full {
    grid-template-columns: minmax(0, 1fr);
    max-width: 100%;
}

.content-body {
    min-width: 0;
    overflow: hidden;
    max-width: 100%;
    background: #ffffff;
    border-radius: 8px;
    padding: 16px 30px 30px 30px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.public-base__back {
    margin-bottom: 18px;
}

.item-meta {
    margin-top: 0;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid #e5e7eb;
}

.created_at {
    font-size: 14px;
    color: #6b7280;
}

.item-title {
    margin-bottom: 25px;
}

.item-title h1 {
    font-size: 28px;
    font-weight: 600;
    color: #1f2937;
    line-height: 1.3;
}

.item-body {
    font-size: 16px;
    line-height: 1.7;
    color: #374151;
}

.toc-sidebar {
    position: relative;
}

.froala-content :deep(h1),
.froala-content :deep(h2),
.froala-content :deep(h3),
.froala-content :deep(h4),
.froala-content :deep(h5),
.froala-content :deep(h6) {
    margin-top: 24px;
    margin-bottom: 16px;
    font-weight: 600;
    line-height: 1.25;
    color: #1f2937;
    scroll-margin-top: 100px;
    scroll-snap-margin-top: 100px;
}

.froala-content :deep(h1) {
    font-size: 24px;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 8px;
}

.froala-content :deep(h2) {
    font-size: 20px;
}

.froala-content :deep(h3) {
    font-size: 18px;
}

.froala-content :deep(p) {
    margin-bottom: 16px;
}

.froala-content :deep(ul),
.froala-content :deep(ol) {
    margin-bottom: 16px;
    padding-left: 24px;
}

.froala-content :deep(li) {
    margin-bottom: 8px;
}

.froala-content :deep(a) {
    color: #3b82f6;
    text-decoration: underline;
    transition: color 0.2s;
}

.froala-content :deep(a:hover) {
    color: #1d4ed8;
}

.froala-content :deep(img) {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 16px auto;
    display: block;
    cursor: pointer;
}

.froala-content :deep(img.fr-dib) {
    display: block;
    width: 100%;
    max-width: 100%;
}

.froala-content :deep(img.fr-fil) {
    float: left;
    width: auto;
    max-width: 100%;
    margin: 0 16px 16px 0;
}

.froala-content :deep(img.fr-fir) {
    float: right;
    width: auto;
    max-width: 100%;
    margin: 0 0 16px 16px;
}

.froala-content :deep(img.fr-fic:not(.fr-fil):not(.fr-fir)) {
    display: block;
    width: 100%;
    max-width: 100%;
    margin: 16px auto;
    float: none;
}

.froala-content :deep(.fr-video) {
    max-width: 100%;
    overflow: hidden;
}

.froala-content :deep(.fr-video iframe),
.froala-content :deep(.fr-video video),
.froala-content :deep(iframe),
.froala-content :deep(video) {
    display: block;
    max-width: 100% !important;
}

.froala-content :deep(video) {
    width: 100% !important;
    height: auto !important;
}

.froala-content :deep(p::after) {
    content: "";
    display: table;
    clear: both;
}

.froala-content :deep(pre) {
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    padding: 16px;
    overflow-x: auto;
    margin-bottom: 16px;
    font-family: 'Courier New', Courier, monospace;
    font-size: 14px;
}

.froala-content :deep(code) {
    background: #f3f4f6;
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Courier New', Courier, monospace;
    font-size: 14px;
}

.froala-content :deep(pre code) {
    background: none;
    padding: 0;
}

.froala-content :deep(table) {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 16px;
}

.froala-content :deep(th),
.froala-content :deep(td) {
    border: 1px solid #e5e7eb;
    padding: 12px;
    text-align: left;
}

.froala-content :deep(th) {
    background: #f9fafb;
    font-weight: 600;
}

.froala-content :deep(blockquote) {
    border-left: 4px solid #3b82f6;
    padding-left: 16px;
    margin: 16px 0;
    color: #4b5563;
    font-style: italic;
}

.froala-content :deep(nav.toc) {
    display: none;
}

@-moz-document url-prefix() {
    .froala-content :deep(table),
    .froala-content :deep(table *) {
        -moz-user-select: text !important;
        user-select: text !important;
    }

    .froala-content :deep(.fr-table-selected),
    .froala-content :deep(.fr-table-selected *) {
        -moz-user-select: text !important;
        caret-color: auto !important;
        user-select: text !important;
    }

    .froala-content :deep(.fr-table-selected *::-moz-selection) {
        background: rgba(25, 118, 210, .35) !important;
        color: inherit !important;
    }
}

.loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    padding: 40px;
}

.loading-spinner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
}

.spinner {
    width: 50px;
    height: 50px;
    animation: spin 1s linear infinite;
}

.spinner-circle {
    stroke: #3b82f6;
    stroke-linecap: round;
    stroke-dasharray: 90, 120;
}

.loading-text {
    font-size: 16px;
    color: #6b7280;
    font-weight: 500;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 1200px) {
    .main-content-wrapper {
        grid-template-columns: 1fr;
        gap: 20px;
    }

    .toc-sidebar {
        display: none;
    }
}

@media (max-width: 960px) {
    .mobile-menu-header {
        display: block;
    }

    .mobile-menu-container {
        display: block;
    }

    .menu-overlay {
        display: block;
    }

    .content-wrapper-inner {
        margin-top: 0;
        padding: 0;
    }
}

@media (max-width: 530px) {
    .public-base__search {
        align-items: stretch;
        flex-direction: column;
    }

    .section-item-container {
        padding: 16px;
    }

    .main-content-wrapper {
        padding: 20px;
    }

    .item-title h1 {
        font-size: 24px;
    }
}
</style>
